import React, { useState } from 'react';
import { Plus, Edit2, Eye, X, Save, Loader2, AlertCircle, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { useCampaigns, useEligibleSessions, createCampaign, updateCampaign } from '../../lib/api';
import { Campaign } from '../../lib/types';
import LoadingState from '../../components/common/LoadingState';
import ErrorState from '../../components/common/ErrorState';
import { formatDate } from '../../lib/utils';

const CampaignsPage: React.FC = () => {
    const { campaigns, isLoading, error, mutate } = useCampaigns();
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
    const [viewingEligibleSessions, setViewingEligibleSessions] = useState<string | null>(null);

    const handleCreate = () => {
        setEditingCampaign(null);
        setIsCreateModalOpen(true);
    };

    const handleEdit = (campaign: Campaign) => {
        setEditingCampaign(campaign);
        setIsCreateModalOpen(true);
    };

    const handleViewEligibleSessions = (campaignId: string) => {
        setViewingEligibleSessions(campaignId);
    };

    if (isLoading) {
        return <LoadingState message="Loading campaigns..." />;
    }

    if (error) {
        return <ErrorState error={error.message || 'An error occurred'} onRetry={() => mutate()} />;
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Campaigns</h1>
                    <p className="text-gray-600">Manage refund campaigns</p>
                </div>
                <button
                    onClick={handleCreate}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700 transition-colors flex items-center gap-2"
                >
                    <Plus className="h-4 w-4" />
                    Create Campaign
                </button>
            </div>

            {/* Campaigns Table */}
            <div className="bg-white border rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Name
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Campaign ID
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Start Date
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    End Date
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Status
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {campaigns.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                                        No campaigns found. Create your first campaign to get started.
                                    </td>
                                </tr>
                            ) : (
                                campaigns.map((campaign) => (
                                    <tr key={campaign.campaignId} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            {campaign.name}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-500">
                                            {campaign.campaignId}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {formatDate(campaign.startDate)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {formatDate(campaign.endDate)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {campaign.isActive ? (
                                                <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 flex items-center gap-1 w-fit">
                                                    <CheckCircle className="h-3 w-3" />
                                                    Active
                                                </span>
                                            ) : (
                                                <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                                                    Inactive
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => handleViewEligibleSessions(campaign.campaignId)}
                                                    className="px-3 py-1 text-xs font-medium text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-md transition-colors flex items-center gap-1"
                                                    title="View eligible sessions"
                                                >
                                                    <Eye className="h-3 w-3" />
                                                    Eligible
                                                </button>
                                                <button
                                                    onClick={() => handleEdit(campaign)}
                                                    className="px-3 py-1 text-xs font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors flex items-center gap-1"
                                                    title="Edit campaign"
                                                >
                                                    <Edit2 className="h-3 w-3" />
                                                    Edit
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Create/Edit Modal */}
            {isCreateModalOpen && (
                <CampaignModal
                    campaign={editingCampaign}
                    onClose={() => {
                        setIsCreateModalOpen(false);
                        setEditingCampaign(null);
                    }}
                    onSuccess={() => {
                        mutate();
                        setIsCreateModalOpen(false);
                        setEditingCampaign(null);
                    }}
                />
            )}

            {/* Eligible Sessions Modal */}
            {viewingEligibleSessions && (
                <EligibleSessionsModal
                    campaignId={viewingEligibleSessions}
                    onClose={() => setViewingEligibleSessions(null)}
                />
            )}
        </div>
    );
};

// Settings structure types
interface MiniguideSettings {
    headline?: string;
    imageBase?: string;
}

interface PopupSettings {
    enabled: boolean;
    text: string;
    miniguide?: MiniguideSettings;
}

interface InfoPageSettings {
    title: string;
    sections: any[];
    miniguide?: MiniguideSettings;
}

interface LanguageSettings {
    popup: PopupSettings;
    infoPage: InfoPageSettings;
}

interface CampaignSettings {
    da?: LanguageSettings;
    en?: LanguageSettings;
}

// Helper function to initialize settings from campaign
const initializeSettings = (campaign: Campaign | null): CampaignSettings => {
    if (!campaign?.settings) {
        return {
            da: {
                popup: { enabled: false, text: '' },
                infoPage: { title: '', sections: [] }
            },
            en: {
                popup: { enabled: false, text: '' },
                infoPage: { title: '', sections: [] }
            }
        };
    }

    const settings = campaign.settings as any;
    return {
        da: {
            popup: {
                enabled: settings.da?.popup?.enabled ?? false,
                text: settings.da?.popup?.text || '',
                miniguide: settings.da?.popup?.miniguide ? {
                    headline: settings.da.popup.miniguide.headline || '',
                    imageBase: settings.da.popup.miniguide.imageBase || ''
                } : undefined
            },
            infoPage: {
                title: settings.da?.infoPage?.title || '',
                sections: settings.da?.infoPage?.sections || [],
                miniguide: settings.da?.infoPage?.miniguide ? {
                    headline: settings.da.infoPage.miniguide.headline || '',
                    imageBase: settings.da.infoPage.miniguide.imageBase || ''
                } : undefined
            }
        },
        en: {
            popup: {
                enabled: settings.en?.popup?.enabled ?? false,
                text: settings.en?.popup?.text || '',
                miniguide: settings.en?.popup?.miniguide ? {
                    headline: settings.en.popup.miniguide.headline || '',
                    imageBase: settings.en.popup.miniguide.imageBase || ''
                } : undefined
            },
            infoPage: {
                title: settings.en?.infoPage?.title || '',
                sections: settings.en?.infoPage?.sections || [],
                miniguide: settings.en?.infoPage?.miniguide ? {
                    headline: settings.en.infoPage.miniguide.headline || '',
                    imageBase: settings.en.infoPage.miniguide.imageBase || ''
                } : undefined
            }
        }
    };
};

// Campaign Create/Edit Modal
interface CampaignModalProps {
    campaign: Campaign | null;
    onClose: () => void;
    onSuccess: () => void;
}

const CampaignModal: React.FC<CampaignModalProps> = ({ campaign, onClose, onSuccess }) => {
    const isEditing = !!campaign;
    const [formData, setFormData] = useState({
        campaignId: campaign?.campaignId || '',
        name: campaign?.name || '',
        startDate: campaign?.startDate ? new Date(campaign.startDate).toISOString().slice(0, 16) : '',
        endDate: campaign?.endDate ? new Date(campaign.endDate).toISOString().slice(0, 16) : '',
        isActive: campaign?.isActive ?? true,
        settings: initializeSettings(campaign),
    });
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
        'da': true,
        'en': true,
    });

    // Validation functions
    const validateImageBase = (imageBase: string): boolean => {
        if (!imageBase.trim()) return true; // Optional field
        return /^[a-zA-Z0-9_]+$/.test(imageBase);
    };

    const validateHeadline = (headline: string): boolean => {
        if (!headline.trim()) return true; // Optional field
        return headline.length <= 200;
    };

    // Build settings object, only including miniguide if it has at least one field
    const buildSettings = (): CampaignSettings => {
        const buildMiniguide = (miniguide?: MiniguideSettings): MiniguideSettings | undefined => {
            if (!miniguide) return undefined;
            const hasHeadline = miniguide.headline?.trim();
            const hasImageBase = miniguide.imageBase?.trim();
            if (!hasHeadline && !hasImageBase) return undefined;
            return {
                ...(hasHeadline && { headline: miniguide.headline }),
                ...(hasImageBase && { imageBase: miniguide.imageBase })
            };
        };

        return {
            da: {
                popup: {
                    enabled: formData.settings.da?.popup?.enabled ?? false,
                    text: formData.settings.da?.popup?.text || '',
                    ...(buildMiniguide(formData.settings.da?.popup?.miniguide) && {
                        miniguide: buildMiniguide(formData.settings.da?.popup?.miniguide)
                    })
                },
                infoPage: {
                    title: formData.settings.da?.infoPage?.title || '',
                    sections: formData.settings.da?.infoPage?.sections || [],
                    ...(buildMiniguide(formData.settings.da?.infoPage?.miniguide) && {
                        miniguide: buildMiniguide(formData.settings.da?.infoPage?.miniguide)
                    })
                }
            },
            en: {
                popup: {
                    enabled: formData.settings.en?.popup?.enabled ?? false,
                    text: formData.settings.en?.popup?.text || '',
                    ...(buildMiniguide(formData.settings.en?.popup?.miniguide) && {
                        miniguide: buildMiniguide(formData.settings.en?.popup?.miniguide)
                    })
                },
                infoPage: {
                    title: formData.settings.en?.infoPage?.title || '',
                    sections: formData.settings.en?.infoPage?.sections || [],
                    ...(buildMiniguide(formData.settings.en?.infoPage?.miniguide) && {
                        miniguide: buildMiniguide(formData.settings.en?.infoPage?.miniguide)
                    })
                }
            }
        };
    };

    const handleSubmit = async () => {
        // Basic validation
        if (!formData.campaignId.trim()) {
            setError('Campaign ID is required');
            return;
        }
        if (!formData.name.trim()) {
            setError('Name is required');
            return;
        }
        if (!formData.startDate) {
            setError('Start date is required');
            return;
        }
        if (!formData.endDate) {
            setError('End date is required');
            return;
        }
        if (new Date(formData.startDate) >= new Date(formData.endDate)) {
            setError('End date must be after start date');
            return;
        }

        // Validate miniguide fields
        const validateMiniguide = (miniguide: MiniguideSettings | undefined, location: string) => {
            if (!miniguide) return true;
            if (miniguide.headline && !validateHeadline(miniguide.headline)) {
                setError(`${location}: Headline must be 200 characters or less`);
                return false;
            }
            if (miniguide.imageBase && !validateImageBase(miniguide.imageBase)) {
                setError(`${location}: Image Base must contain only alphanumeric characters and underscores`);
                return false;
            }
            return true;
        };

        if (!validateMiniguide(formData.settings.da?.popup?.miniguide, 'Danish Popup Miniguide')) return;
        if (!validateMiniguide(formData.settings.da?.infoPage?.miniguide, 'Danish InfoPage Miniguide')) return;
        if (!validateMiniguide(formData.settings.en?.popup?.miniguide, 'English Popup Miniguide')) return;
        if (!validateMiniguide(formData.settings.en?.infoPage?.miniguide, 'English InfoPage Miniguide')) return;

        setIsSaving(true);
        setError(null);

        try {
            const settings = buildSettings();
            const campaignData = {
                campaignId: formData.campaignId,
                name: formData.name,
                startDate: new Date(formData.startDate).toISOString(),
                endDate: new Date(formData.endDate).toISOString(),
                isActive: formData.isActive,
                settings,
            };

            if (isEditing && campaign) {
                await updateCampaign(campaign.campaignId, campaignData);
            } else {
                await createCampaign(campaignData);
            }
            onSuccess();
        } catch (err: any) {
            setError(err.message || 'Failed to save campaign');
        } finally {
            setIsSaving(false);
        }
    };

    const toggleSection = (section: string) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    // Helper to update settings
    const updateSettings = (path: string[], value: any) => {
        setFormData(prev => {
            const newSettings = JSON.parse(JSON.stringify(prev.settings)); // Deep clone
            
            // Navigate to the parent object
            let current: any = newSettings;
            for (let i = 0; i < path.length - 1; i++) {
                if (!current[path[i]]) {
                    current[path[i]] = {};
                }
                current = current[path[i]];
            }
            
            // Set the value on the parent
            const lastKey = path[path.length - 1];
            current[lastKey] = value;
            
            return { ...prev, settings: newSettings };
        });
    };

    // Helper to update miniguide field
    const updateMiniguide = (lang: 'da' | 'en', section: 'popup' | 'infoPage', field: 'headline' | 'imageBase', value: string) => {
        setFormData(prev => {
            const newSettings = JSON.parse(JSON.stringify(prev.settings));
            if (!newSettings[lang]) {
                newSettings[lang] = {
                    popup: { enabled: false, text: '' },
                    infoPage: { title: '', sections: [] }
                };
            }
            if (!newSettings[lang][section]) {
                if (section === 'popup') {
                    newSettings[lang][section] = { enabled: false, text: '' };
                } else {
                    newSettings[lang][section] = { title: '', sections: [] };
                }
            }
            if (!newSettings[lang][section].miniguide) {
                newSettings[lang][section].miniguide = {};
            }
            newSettings[lang][section].miniguide[field] = value;
            return { ...prev, settings: newSettings };
        });
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
                <div className="flex items-center justify-between p-6 border-b">
                    <h2 className="text-xl font-semibold text-gray-900">
                        {isEditing ? 'Edit Campaign' : 'Create Campaign'}
                    </h2>
                    <button
                        onClick={onClose}
                        disabled={isSaving}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)] space-y-4">
                    {error && (
                        <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                            <div className="flex items-center gap-2">
                                <AlertCircle className="h-5 w-5 text-red-600" />
                                <p className="text-sm text-red-800">{error}</p>
                            </div>
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Campaign ID <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={formData.campaignId}
                            onChange={(e) => setFormData({ ...formData, campaignId: e.target.value })}
                            disabled={isEditing || isSaving}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                            placeholder="e.g., january-2025"
                        />
                        {isEditing && (
                            <p className="mt-1 text-xs text-gray-500">Campaign ID cannot be changed</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Name <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            disabled={isSaving}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
                            placeholder="e.g., Januar kampagne"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Start Date <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="datetime-local"
                                value={formData.startDate}
                                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                                disabled={isSaving}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                End Date <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="datetime-local"
                                value={formData.endDate}
                                onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                                disabled={isSaving}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                checked={formData.isActive}
                                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                                disabled={isSaving}
                                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-50"
                            />
                            <span className="text-sm font-medium text-gray-700">Active</span>
                        </label>
                    </div>

                    {/* Settings Section */}
                    <div className="border-t pt-4 mt-4">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Settings</h3>
                        
                        {/* Danish Settings */}
                        <div className="mb-6 border border-gray-200 rounded-lg">
                            <button
                                type="button"
                                onClick={() => toggleSection('da')}
                                className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 flex items-center justify-between text-left font-medium text-gray-900 rounded-t-lg"
                            >
                                <span>Danish Settings</span>
                                {expandedSections['da'] ? (
                                    <ChevronUp className="h-5 w-5" />
                                ) : (
                                    <ChevronDown className="h-5 w-5" />
                                )}
                            </button>
                            
                            {expandedSections['da'] && (
                                <div className="p-4 space-y-6">
                                    {/* Danish Popup Section */}
                                    <div className="border-b pb-4">
                                        <h4 className="text-md font-medium text-gray-800 mb-3">Popup</h4>
                                        <div className="space-y-3">
                                            <div>
                                                <label className="flex items-center gap-2">
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.settings.da?.popup?.enabled ?? false}
                                                        onChange={(e) => updateSettings(['da', 'popup', 'enabled'], e.target.checked)}
                                                        disabled={isSaving}
                                                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-50"
                                                    />
                                                    <span className="text-sm font-medium text-gray-700">Enabled</span>
                                                </label>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Text
                                                </label>
                                                <textarea
                                                    value={formData.settings.da?.popup?.text || ''}
                                                    onChange={(e) => updateSettings(['da', 'popup', 'text'], e.target.value)}
                                                    disabled={isSaving}
                                                    rows={3}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
                                                    placeholder="Popup text..."
                                                />
                                            </div>
                                            {/* Miniguide for Danish Popup */}
                                            <div className="ml-4 pl-4 border-l-2 border-indigo-200 bg-indigo-50 p-3 rounded">
                                                <h5 className="text-sm font-medium text-gray-800 mb-2">Miniguide (Optional)</h5>
                                                <p className="text-xs text-gray-600 mb-3">Miniguide vises nederst i popup'en med headline tekst og et billede</p>
                                                <div className="space-y-3">
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            Headline
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={formData.settings.da?.popup?.miniguide?.headline || ''}
                                                            onChange={(e) => updateMiniguide('da', 'popup', 'headline', e.target.value)}
                                                            disabled={isSaving}
                                                            maxLength={200}
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
                                                            placeholder="Når du har fundet den plads du vil parkere på..."
                                                        />
                                                        <p className="mt-1 text-xs text-gray-500">
                                                            {formData.settings.da?.popup?.miniguide?.headline?.length || 0}/200 characters
                                                        </p>
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            Image Base
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={formData.settings.da?.popup?.miniguide?.imageBase || ''}
                                                            onChange={(e) => updateMiniguide('da', 'popup', 'imageBase', e.target.value)}
                                                            disabled={isSaving}
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
                                                            placeholder="pay"
                                                        />
                                                        <p className="mt-1 text-xs text-gray-500">
                                                            Billederne skal allerede eksistere på backend'en med navnene {formData.settings.da?.popup?.miniguide?.imageBase || '{imageBase}'}_lightmode.png og {formData.settings.da?.popup?.miniguide?.imageBase || '{imageBase}'}_darkmode.png
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Danish InfoPage Section */}
                                    <div className="border-t pt-4">
                                        <h4 className="text-md font-medium text-gray-800 mb-3">Info Page</h4>
                                        <div className="space-y-3">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Title
                                                </label>
                                                <input
                                                    type="text"
                                                    value={formData.settings.da?.infoPage?.title || ''}
                                                    onChange={(e) => updateSettings(['da', 'infoPage', 'title'], e.target.value)}
                                                    disabled={isSaving}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
                                                    placeholder="Info page title..."
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Sections (JSON)
                                                </label>
                                                <textarea
                                                    value={JSON.stringify(formData.settings.da?.infoPage?.sections || [], null, 2)}
                                                    onChange={(e) => {
                                                        try {
                                                            const parsed = e.target.value.trim() ? JSON.parse(e.target.value) : [];
                                                            updateSettings(['da', 'infoPage', 'sections'], parsed);
                                                        } catch {
                                                            // Invalid JSON, ignore
                                                        }
                                                    }}
                                                    disabled={isSaving}
                                                    rows={4}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
                                                    placeholder='[{"type": "text", "content": "..."}]'
                                                />
                                                <p className="mt-1 text-xs text-gray-500">JSON array of sections</p>
                                            </div>
                                            {/* Miniguide for Danish InfoPage */}
                                            <div className="ml-4 pl-4 border-l-2 border-indigo-200 bg-indigo-50 p-3 rounded">
                                                <h5 className="text-sm font-medium text-gray-800 mb-2">Miniguide (Optional)</h5>
                                                <p className="text-xs text-gray-600 mb-3">Miniguide vises nederst på info siden med headline tekst og et billede</p>
                                                <div className="space-y-3">
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            Headline
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={formData.settings.da?.infoPage?.miniguide?.headline || ''}
                                                            onChange={(e) => updateMiniguide('da', 'infoPage', 'headline', e.target.value)}
                                                            disabled={isSaving}
                                                            maxLength={200}
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
                                                            placeholder="Når du har fundet den plads du vil parkere på..."
                                                        />
                                                        <p className="mt-1 text-xs text-gray-500">
                                                            {formData.settings.da?.infoPage?.miniguide?.headline?.length || 0}/200 characters
                                                        </p>
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            Image Base
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={formData.settings.da?.infoPage?.miniguide?.imageBase || ''}
                                                            onChange={(e) => updateMiniguide('da', 'infoPage', 'imageBase', e.target.value)}
                                                            disabled={isSaving}
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
                                                            placeholder="pay"
                                                        />
                                                        <p className="mt-1 text-xs text-gray-500">
                                                            Billederne skal allerede eksistere på backend'en med navnene {formData.settings.da?.infoPage?.miniguide?.imageBase || '{imageBase}'}_lightmode.png og {formData.settings.da?.infoPage?.miniguide?.imageBase || '{imageBase}'}_darkmode.png
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* English Settings */}
                        <div className="mb-6 border border-gray-200 rounded-lg">
                            <button
                                type="button"
                                onClick={() => toggleSection('en')}
                                className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 flex items-center justify-between text-left font-medium text-gray-900 rounded-t-lg"
                            >
                                <span>English Settings</span>
                                {expandedSections['en'] ? (
                                    <ChevronUp className="h-5 w-5" />
                                ) : (
                                    <ChevronDown className="h-5 w-5" />
                                )}
                            </button>
                            
                            {expandedSections['en'] && (
                                <div className="p-4 space-y-6">
                                    {/* English Popup Section */}
                                    <div className="border-b pb-4">
                                        <h4 className="text-md font-medium text-gray-800 mb-3">Popup</h4>
                                        <div className="space-y-3">
                                            <div>
                                                <label className="flex items-center gap-2">
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.settings.en?.popup?.enabled ?? false}
                                                        onChange={(e) => updateSettings(['en', 'popup', 'enabled'], e.target.checked)}
                                                        disabled={isSaving}
                                                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-50"
                                                    />
                                                    <span className="text-sm font-medium text-gray-700">Enabled</span>
                                                </label>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Text
                                                </label>
                                                <textarea
                                                    value={formData.settings.en?.popup?.text || ''}
                                                    onChange={(e) => updateSettings(['en', 'popup', 'text'], e.target.value)}
                                                    disabled={isSaving}
                                                    rows={3}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
                                                    placeholder="Popup text..."
                                                />
                                            </div>
                                            {/* Miniguide for English Popup */}
                                            <div className="ml-4 pl-4 border-l-2 border-indigo-200 bg-indigo-50 p-3 rounded">
                                                <h5 className="text-sm font-medium text-gray-800 mb-2">Miniguide (Optional)</h5>
                                                <p className="text-xs text-gray-600 mb-3">Miniguide is displayed at the bottom of the popup with headline text and an image</p>
                                                <div className="space-y-3">
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            Headline
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={formData.settings.en?.popup?.miniguide?.headline || ''}
                                                            onChange={(e) => updateMiniguide('en', 'popup', 'headline', e.target.value)}
                                                            disabled={isSaving}
                                                            maxLength={200}
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
                                                            placeholder="When you have found the spot you want to park at..."
                                                        />
                                                        <p className="mt-1 text-xs text-gray-500">
                                                            {formData.settings.en?.popup?.miniguide?.headline?.length || 0}/200 characters
                                                        </p>
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            Image Base
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={formData.settings.en?.popup?.miniguide?.imageBase || ''}
                                                            onChange={(e) => updateMiniguide('en', 'popup', 'imageBase', e.target.value)}
                                                            disabled={isSaving}
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
                                                            placeholder="pay"
                                                        />
                                                        <p className="mt-1 text-xs text-gray-500">
                                                            Images must already exist on the backend with names {formData.settings.en?.popup?.miniguide?.imageBase || '{imageBase}'}_lightmode.png and {formData.settings.en?.popup?.miniguide?.imageBase || '{imageBase}'}_darkmode.png
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* English InfoPage Section */}
                                    <div className="border-t pt-4">
                                        <h4 className="text-md font-medium text-gray-800 mb-3">Info Page</h4>
                                        <div className="space-y-3">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Title
                                                </label>
                                                <input
                                                    type="text"
                                                    value={formData.settings.en?.infoPage?.title || ''}
                                                    onChange={(e) => updateSettings(['en', 'infoPage', 'title'], e.target.value)}
                                                    disabled={isSaving}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
                                                    placeholder="Info page title..."
                                                />
                                            </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Sections (JSON)
                        </label>
                        <textarea
                                                    value={JSON.stringify(formData.settings.en?.infoPage?.sections || [], null, 2)}
                                                    onChange={(e) => {
                                                        try {
                                                            const parsed = e.target.value.trim() ? JSON.parse(e.target.value) : [];
                                                            updateSettings(['en', 'infoPage', 'sections'], parsed);
                                                        } catch {
                                                            // Invalid JSON, ignore
                                                        }
                                                    }}
                            disabled={isSaving}
                                                    rows={4}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
                                                    placeholder='[{"type": "text", "content": "..."}]'
                                                />
                                                <p className="mt-1 text-xs text-gray-500">JSON array of sections</p>
                                            </div>
                                            {/* Miniguide for English InfoPage */}
                                            <div className="ml-4 pl-4 border-l-2 border-indigo-200 bg-indigo-50 p-3 rounded">
                                                <h5 className="text-sm font-medium text-gray-800 mb-2">Miniguide (Optional)</h5>
                                                <p className="text-xs text-gray-600 mb-3">Miniguide is displayed at the bottom of the info page with headline text and an image</p>
                                                <div className="space-y-3">
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            Headline
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={formData.settings.en?.infoPage?.miniguide?.headline || ''}
                                                            onChange={(e) => updateMiniguide('en', 'infoPage', 'headline', e.target.value)}
                                                            disabled={isSaving}
                                                            maxLength={200}
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
                                                            placeholder="When you have found the spot you want to park at..."
                                                        />
                                                        <p className="mt-1 text-xs text-gray-500">
                                                            {formData.settings.en?.infoPage?.miniguide?.headline?.length || 0}/200 characters
                                                        </p>
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            Image Base
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={formData.settings.en?.infoPage?.miniguide?.imageBase || ''}
                                                            onChange={(e) => updateMiniguide('en', 'infoPage', 'imageBase', e.target.value)}
                                                            disabled={isSaving}
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
                                                            placeholder="pay"
                                                        />
                                                        <p className="mt-1 text-xs text-gray-500">
                                                            Images must already exist on the backend with names {formData.settings.en?.infoPage?.miniguide?.imageBase || '{imageBase}'}_lightmode.png and {formData.settings.en?.infoPage?.miniguide?.imageBase || '{imageBase}'}_darkmode.png
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="flex items-center justify-end gap-3 p-6 border-t bg-gray-50">
                    <button
                        onClick={onClose}
                        disabled={isSaving}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={isSaving}
                        className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                    >
                        {isSaving ? (
                            <>
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Saving...
                            </>
                        ) : (
                            <>
                                <Save className="h-4 w-4" />
                                {isEditing ? 'Update' : 'Create'}
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

// Eligible Sessions Modal
interface EligibleSessionsModalProps {
    campaignId: string;
    onClose: () => void;
}

const EligibleSessionsModal: React.FC<EligibleSessionsModalProps> = ({ campaignId, onClose }) => {
    const { sessions, total, isLoading } = useEligibleSessions(campaignId, 100, 0);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
                <div className="flex items-center justify-between p-6 border-b">
                    <div>
                        <h2 className="text-xl font-semibold text-gray-900">Eligible Sessions</h2>
                        <p className="text-sm text-gray-600 mt-1">
                            Sessions eligible for refund in this campaign
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
                    {isLoading ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
                        </div>
                    ) : (
                        <>
                            <div className="mb-4 text-sm text-gray-600">
                                Total eligible sessions: <span className="font-medium">{total}</span>
                            </div>
                            {sessions.length === 0 ? (
                                <div className="text-center py-8 text-gray-500">
                                    No eligible sessions found for this campaign
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Session ID
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Status
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Start Time
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Total Amount
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {sessions.map((session) => (
                                                <tr key={session.sessionId} className="hover:bg-gray-50">
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                                                        {session.sessionId.substring(0, 16)}...
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                        {session.status}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                        {formatDate(session.startTime)}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                        {session.pricing.total.toFixed(2)} DKK
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </>
                    )}
                </div>

                <div className="flex items-center justify-end p-6 border-t bg-gray-50">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CampaignsPage;

